// Date helpers that work on plain YYYY-MM-DD strings in UTC, so the output is the
// same on the server and in every visitor's browser regardless of time zone.

export const TODAY = '2026-11-03'

const DAY_MS = 86400000

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']

const MONTHS_LONG = [
    'Januari',
    'Februari',
    'Maret',
    'April',
    'Mei',
    'Juni',
    'Juli',
    'Agustus',
    'September',
    'Oktober',
    'November',
    'Desember'
]

const pad = (value: number): string => String(value).padStart(2, '0')

const parts = (iso: string): [number, number, number] => {
    const [year, month, day] = iso.split('-').map(Number)
    return [year, month, day]
}

export const parseISODate = (iso: string): number => {
    const [year, month, day] = parts(iso)
    return Date.UTC(year, month - 1, day)
}

export const toISODate = (time: number): string => {
    const date = new Date(time)
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

export const addDays = (iso: string, days: number): string => toISODate(parseISODate(iso) + days * DAY_MS)

export const daysBetween = (from: string, to: string): number => Math.round((parseISODate(to) - parseISODate(from)) / DAY_MS)

export const daysInMonth = (year: number, month: number): number => new Date(Date.UTC(year, month, 0)).getUTCDate()

export const addMonths = (iso: string, months: number): string => {
    const [year, month, day] = parts(iso)
    const target = new Date(Date.UTC(year, month - 1 + months, 1))
    const targetYear = target.getUTCFullYear()
    const targetMonth = target.getUTCMonth() + 1
    const safeDay = Math.min(day, daysInMonth(targetYear, targetMonth))
    return `${targetYear}-${pad(targetMonth)}-${pad(safeDay)}`
}

export const startOfMonth = (iso: string): string => `${iso.slice(0, 7)}-01`

export const endOfMonth = (iso: string): string => {
    const [year, month] = parts(iso)
    return `${year}-${pad(month)}-${pad(daysInMonth(year, month))}`
}

export const getDayOfMonth = (iso: string): number => parts(iso)[2]

// 3 Nov 2026
export const formatDate = (iso: string): string => {
    const [year, month, day] = parts(iso)
    return `${day} ${MONTHS_SHORT[month - 1]} ${year}`
}

// 3 November 2026
export const formatDateLong = (iso: string): string => {
    const [year, month, day] = parts(iso)
    return `${day} ${MONTHS_LONG[month - 1]} ${year}`
}

// 3 Nov
export const formatDateShort = (iso: string): string => {
    const [, month, day] = parts(iso)
    return `${day} ${MONTHS_SHORT[month - 1]}`
}

// Nov 2026
export const formatMonth = (iso: string): string => {
    const [year, month] = parts(iso)
    return `${MONTHS_SHORT[month - 1]} ${year}`
}

// November 2026
export const formatMonthLong = (iso: string): string => {
    const [year, month] = parts(iso)
    return `${MONTHS_LONG[month - 1]} ${year}`
}

// Nov
export const formatMonthShort = (iso: string): string => MONTHS_SHORT[parts(iso)[1] - 1]

const WEEKDAYS = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu']

// Selasa, 3 November 2026
export const formatWeekdayDate = (iso: string): string => `${WEEKDAYS[new Date(parseISODate(iso)).getUTCDay()]}, ${formatDateLong(iso)}`

export const formatRelative = (iso: string): string => {
    const diff = daysBetween(iso, TODAY)

    if (diff <= 0) return 'Hari ini'
    if (diff === 1) return 'Kemarin'
    if (diff < 7) return `${diff} hari lalu`
    if (diff < 30) return `${Math.floor(diff / 7)} minggu lalu`
    if (diff < 365) return `${Math.floor(diff / 30)} bulan lalu`

    return `${Math.floor(diff / 365)} tahun lalu`
}
